import { useState, useEffect, useCallback, useRef } from 'react';
import { getOrganisations, getSites, createSite, updateSite, deleteSite, getLocationTreeBySite, createLocation, updateLocation, deleteLocation, reorderLocationsBySite } from '../api';
import EmptyState from '../components/EmptyState';
import Breadcrumb from '../components/Breadcrumb';

function SiteForm({ organisationId, initial, onSave, onCancel }) {
  const [name, setName] = useState(initial?.name || '');
  const [address, setAddress] = useState(initial?.address || '');
  const [sortOrder, setSortOrder] = useState(initial?.sortOrder ?? '');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const data = { organisationId, name, address: address || null, sortOrder: sortOrder !== '' ? Number(sortOrder) : 0 };
      if (initial?.id) {
        await updateSite(initial.id, data);
      } else {
        await createSite(data);
      }
      onSave();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '1rem', marginTop: '0.75rem', marginBottom: '0.75rem' }}>
      {error && <div className="alert alert-error" style={{ marginBottom: '0.75rem' }}>{error}</div>}
      <div className="form-group">
        <label>Site Name</label>
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Hospital, Campus, Facility" required />
      </div>
      <div className="form-row">
        <div className="form-group" style={{ flex: 2 }}>
          <label>Address (optional)</label>
          <input type="text" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="e.g. 123 High Street, London" />
        </div>
        <div className="form-group">
          <label>Sort Order (optional)</label>
          <input type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} placeholder="0" />
        </div>
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
        <button type="submit" className="btn btn-primary">{initial?.id ? 'Save Changes' : 'Add Site'}</button>
        <button type="button" className="btn btn-outline" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

function LocationForm({ organisationId, siteId, parentId, initial, onSave, onCancel }) {
  const [name, setName] = useState(initial?.name || '');
  const [locationType, setLocationType] = useState(initial?.locationType || '');
  const [floorNumber, setFloorNumber] = useState(initial?.floorNumber ?? '');
  const [sortOrder, setSortOrder] = useState(initial?.sortOrder ?? '');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const data = { organisationId, siteId, parentId: parentId || null, name, locationType: locationType || null, floorNumber: floorNumber !== '' ? Number(floorNumber) : null, sortOrder: sortOrder !== '' ? Number(sortOrder) : 0 };
      if (initial?.id) {
        await updateLocation(initial.id, data);
      } else {
        await createLocation(data);
      }
      onSave();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '1rem', marginTop: '0.75rem', marginBottom: '0.75rem' }}>
      {error && <div className="alert alert-error" style={{ marginBottom: '0.75rem' }}>{error}</div>}
      <div className="form-group">
        <label>Location Name</label>
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Building, Floor, Wing" required />
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>Type (optional)</label>
          <input type="text" value={locationType} onChange={(e) => setLocationType(e.target.value)} placeholder="e.g. Building, Floor, Wing" />
        </div>
        <div className="form-group">
          <label>Floor</label>
          <select value={floorNumber} onChange={(e) => setFloorNumber(e.target.value)}>
            <option value="">—</option>
            {Array.from({ length: 12 }, (_, i) => i - 1).map((f) => (
              <option key={f} value={f}>{f < 0 ? `B${Math.abs(f)}` : f === 0 ? 'Ground' : `Floor ${f}`}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Sort Order (optional)</label>
          <input type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} placeholder="0" />
        </div>
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
        <button type="submit" className="btn btn-primary">{initial?.id ? 'Save Changes' : 'Add Location'}</button>
        <button type="button" className="btn btn-outline" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

function LocationNode({ node, depth, organisationId, siteId, onRefresh, dragState, onDragStart, onDragOver, onDragEnd, onDrop }) {
  const [editing, setEditing] = useState(false);
  const [addingChild, setAddingChild] = useState(false);
  const rowRef = useRef(null);

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${node.name}"?`)) return;
    try {
      await deleteLocation(node.id);
      onRefresh();
    } catch (err) {
      alert(err.message);
    }
  };

  const isDragging = dragState?.dragId === node.id;
  const isOverTop = dragState?.overId === node.id && dragState?.position === 'top';
  const isOverBottom = dragState?.overId === node.id && dragState?.position === 'bottom';

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!dragState || dragState.dragId === node.id) return;
    if (dragState.dragParentId !== node.parentId) return;
    const rect = rowRef.current.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const position = e.clientY < midY ? 'top' : 'bottom';
    onDragOver(node.id, position);
  };

  return (
    <div>
      <div
        ref={rowRef}
        draggable
        onDragStart={(e) => {
          e.stopPropagation();
          onDragStart(node.id, node.parentId);
          e.dataTransfer.effectAllowed = 'move';
        }}
        onDragOver={handleDragOver}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDrop(node.id);
        }}
        onDragEnd={onDragEnd}
        className={`loc-row${isDragging ? ' loc-row-dragging' : ''}`}
        style={{ paddingLeft: depth * 24 + 8 }}
      >
        {isOverTop && <div className="loc-drop-indicator loc-drop-top" />}
        <span className="loc-drag-handle" title="Drag to reorder">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="9" cy="6" r="1.5" fill="currentColor" /><circle cx="15" cy="6" r="1.5" fill="currentColor" />
            <circle cx="9" cy="12" r="1.5" fill="currentColor" /><circle cx="15" cy="12" r="1.5" fill="currentColor" />
            <circle cx="9" cy="18" r="1.5" fill="currentColor" /><circle cx="15" cy="18" r="1.5" fill="currentColor" />
          </svg>
        </span>
        <span style={{ flex: 1 }}>
          <strong>{node.name}</strong>
          {node.locationType && <span style={{ color: '#6b7280', marginLeft: 8, fontSize: '0.85rem' }}>({node.locationType})</span>}
          {node.floorNumber != null && <span style={{ color: '#6b7280', marginLeft: 8, fontSize: '0.85rem' }}>{node.floorNumber < 0 ? `B${Math.abs(node.floorNumber)}` : node.floorNumber === 0 ? 'Ground' : `Floor ${node.floorNumber}`}</span>}
        </span>
        <button className="btn btn-outline" style={{ fontSize: '0.75rem', padding: '2px 8px' }} onClick={() => { setEditing(true); setAddingChild(false); }}>Edit</button>
        <button className="btn btn-outline" style={{ fontSize: '0.75rem', padding: '2px 8px' }} onClick={() => { setAddingChild(true); setEditing(false); }}>Add Location</button>
        <button className="btn btn-outline" style={{ fontSize: '0.75rem', padding: '2px 8px', color: '#ef4444' }} onClick={handleDelete}>Delete</button>
        {isOverBottom && <div className="loc-drop-indicator loc-drop-bottom" />}
      </div>

      {editing && (
        <div style={{ paddingLeft: depth * 24 }}>
          <LocationForm
            organisationId={organisationId}
            siteId={siteId}
            parentId={node.parentId}
            initial={node}
            onSave={() => { setEditing(false); onRefresh(); }}
            onCancel={() => setEditing(false)}
          />
        </div>
      )}

      {addingChild && (
        <div style={{ paddingLeft: (depth + 1) * 24 }}>
          <LocationForm
            organisationId={organisationId}
            siteId={siteId}
            parentId={node.id}
            onSave={() => { setAddingChild(false); onRefresh(); }}
            onCancel={() => setAddingChild(false)}
          />
        </div>
      )}

      {node.children && node.children.map((child) => (
        <LocationNode
          key={child.id}
          node={child}
          depth={depth + 1}
          organisationId={organisationId}
          siteId={siteId}
          onRefresh={onRefresh}
          dragState={dragState}
          onDragStart={onDragStart}
          onDragOver={onDragOver}
          onDragEnd={onDragEnd}
          onDrop={onDrop}
        />
      ))}
    </div>
  );
}

function flattenTree(nodes) {
  const map = {};
  const walk = (list, parentId) => {
    list.forEach((n) => {
      map[n.id] = { ...n, parentId };
      if (n.children) walk(n.children, n.id);
    });
  };
  walk(nodes, null);
  return map;
}

function reorderTree(tree, parentId, dragId, targetId, position) {
  const cloneList = (nodes, pid) => {
    if (pid === parentId) {
      const filtered = nodes.filter((n) => n.id !== dragId);
      const dragNode = nodes.find((n) => n.id === dragId);
      if (!dragNode) return nodes.map((n) => ({ ...n, children: cloneList(n.children || [], n.id) }));

      const result = [];
      for (const n of filtered) {
        if (n.id === targetId && position === 'top') {
          result.push({ ...dragNode, children: cloneList(dragNode.children || [], dragNode.id) });
        }
        result.push({ ...n, children: cloneList(n.children || [], n.id) });
        if (n.id === targetId && position === 'bottom') {
          result.push({ ...dragNode, children: cloneList(dragNode.children || [], dragNode.id) });
        }
      }
      return result;
    }
    return nodes.map((n) => ({ ...n, children: cloneList(n.children || [], n.id) }));
  };
  return cloneList(tree, parentId);
}

function collectOrderChanges(currentTree, originalTree) {
  const changes = [];
  const walkBoth = (current, original) => {
    current.forEach((node, idx) => {
      const origNode = original?.find((o) => o.id === node.id);
      const origIdx = original?.indexOf(origNode);
      if (origIdx !== idx || origIdx === -1) {
        changes.push({ id: node.id, sortOrder: idx });
      }
      if (node.children?.length) {
        walkBoth(node.children, origNode?.children || []);
      }
    });
  };
  walkBoth(currentTree, originalTree);
  return changes;
}

function SiteRow({ site, organisationId, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const [editingSite, setEditingSite] = useState(false);
  const [tree, setTree] = useState([]);
  const [originalTree, setOriginalTree] = useState([]);
  const [loadingTree, setLoadingTree] = useState(false);
  const [addingLocation, setAddingLocation] = useState(false);
  const [dragState, setDragState] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  const loadTree = useCallback(() => {
    if (!expanded) return;
    setLoadingTree(true);
    getLocationTreeBySite(site.id)
      .then((data) => {
        setTree(data);
        setOriginalTree(JSON.parse(JSON.stringify(data)));
      })
      .catch(() => {})
      .finally(() => setLoadingTree(false));
  }, [expanded, site.id]);

  useEffect(() => {
    loadTree();
  }, [loadTree]);

  const handleDeleteSite = async () => {
    if (!window.confirm(`Delete site "${site.name}"?`)) return;
    try {
      await deleteSite(site.id);
      onRefresh();
    } catch (err) {
      alert(err.message);
    }
  };

  const pendingChanges = collectOrderChanges(tree, originalTree);
  const hasChanges = pendingChanges.length > 0;

  const handleDragStart = (id, parentId) => {
    setDragState({ dragId: id, dragParentId: parentId, overId: null, position: null });
  };
  const handleDragOver = (overId, position) => {
    setDragState((prev) => prev ? { ...prev, overId, position } : null);
  };
  const handleDragEnd = () => { setDragState(null); };
  const handleDrop = (targetId) => {
    if (!dragState || dragState.dragId === targetId) { setDragState(null); return; }
    const flatMap = flattenTree(tree);
    const dragNode = flatMap[dragState.dragId];
    const targetNode = flatMap[targetId];
    if (!dragNode || !targetNode || dragNode.parentId !== targetNode.parentId) { setDragState(null); return; }
    const newTree = reorderTree(tree, dragNode.parentId, dragState.dragId, targetId, dragState.position);
    setTree(newTree);
    setDragState(null);
  };

  const handleSaveOrder = async () => {
    setSaving(true);
    setSaveMessage('');
    try {
      await reorderLocationsBySite(site.id, pendingChanges);
      setSaveMessage('Order saved');
      loadTree();
      setTimeout(() => setSaveMessage(''), 2000);
    } catch (err) {
      setSaveMessage('Error: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelReorder = () => {
    setTree(JSON.parse(JSON.stringify(originalTree)));
  };

  return (
    <div style={{ marginBottom: '0.5rem', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1rem',
          background: expanded ? '#f0f9ff' : '#fff', cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <span style={{ fontSize: '0.8rem', color: '#6b7280', width: 16, textAlign: 'center' }}>
          {expanded ? '\u25BC' : '\u25B6'}
        </span>
        <span style={{ flex: 1 }}>
          <strong>{site.name}</strong>
          {site.address && <span style={{ color: '#6b7280', marginLeft: 8, fontSize: '0.85rem' }}>{site.address}</span>}
        </span>
        <button className="btn btn-outline" style={{ fontSize: '0.75rem', padding: '2px 8px' }} onClick={(e) => { e.stopPropagation(); setEditingSite(true); }}>Edit</button>
        <button className="btn btn-outline" style={{ fontSize: '0.75rem', padding: '2px 8px', color: '#ef4444' }} onClick={(e) => { e.stopPropagation(); handleDeleteSite(); }}>Delete</button>
      </div>

      {editingSite && (
        <div style={{ padding: '0 1rem 1rem' }}>
          <SiteForm
            organisationId={organisationId}
            initial={site}
            onSave={() => { setEditingSite(false); onRefresh(); }}
            onCancel={() => setEditingSite(false)}
          />
        </div>
      )}

      {expanded && (
        <div style={{ padding: '0.5rem 1rem 1rem', borderTop: '1px solid #e5e7eb', background: '#fafbfc' }}>
          {loadingTree && <p style={{ color: '#888', fontSize: '0.85rem' }}>Loading locations...</p>}

          {!loadingTree && hasChanges && (
            <div className="loc-save-bar">
              <span className="loc-save-bar-text">You have unsaved ordering changes</span>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button className="btn btn-outline" onClick={handleCancelReorder} disabled={saving}>Cancel</button>
                <button className="btn btn-primary" onClick={handleSaveOrder} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Order'}
                </button>
              </div>
            </div>
          )}

          {saveMessage && !hasChanges && (
            <div className="alert alert-success" style={{ marginBottom: '0.5rem' }}>{saveMessage}</div>
          )}

          {!loadingTree && tree.length > 0 && (
            <div style={{ marginBottom: '0.5rem' }}>
              {tree.map((node) => (
                <LocationNode
                  key={node.id}
                  node={node}
                  depth={0}
                  organisationId={organisationId}
                  siteId={site.id}
                  onRefresh={loadTree}
                  dragState={dragState}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDragEnd={handleDragEnd}
                  onDrop={handleDrop}
                />
              ))}
            </div>
          )}

          {!loadingTree && tree.length === 0 && !addingLocation && (
            <p style={{ color: '#888', fontSize: '0.85rem', marginBottom: '0.5rem' }}>No locations in this site.</p>
          )}

          {addingLocation ? (
            <LocationForm
              organisationId={organisationId}
              siteId={site.id}
              parentId={null}
              onSave={() => { setAddingLocation(false); loadTree(); }}
              onCancel={() => setAddingLocation(false)}
            />
          ) : (
            <button className="btn btn-outline" style={{ fontSize: '0.8rem', padding: '4px 12px' }} onClick={() => setAddingLocation(true)}>Add Location</button>
          )}
        </div>
      )}
    </div>
  );
}

export default function LocationsPage() {
  const [orgs, setOrgs] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addingSite, setAddingSite] = useState(false);

  useEffect(() => {
    getOrganisations().then(setOrgs).catch(() => {});
  }, []);

  const loadSites = useCallback(() => {
    if (!selectedOrg) return;
    setLoading(true);
    getSites(selectedOrg)
      .then(setSites)
      .catch(() => setSites([]))
      .finally(() => setLoading(false));
  }, [selectedOrg]);

  useEffect(() => {
    loadSites();
  }, [loadSites]);

  return (
    <div className="page-container">
      <div className="form-card">
        <Breadcrumb items={[
          { label: 'Dashboard', to: '/' },
          { label: 'Sites' },
        ]} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2>Sites</h2>
        </div>

        <div className="form-group">
          <label>Organisation</label>
          <select value={selectedOrg} onChange={(e) => { setSelectedOrg(e.target.value); setSites([]); setAddingSite(false); }}>
            <option value="">Select organisation...</option>
            {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>

        {loading && <p>Loading...</p>}

        {!loading && selectedOrg && (
          <>
            {sites.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                {sites.map((site) => (
                  <SiteRow
                    key={site.id}
                    site={site}
                    organisationId={Number(selectedOrg)}
                    onRefresh={loadSites}
                  />
                ))}
              </div>
            )}

            {!loading && selectedOrg && sites.length === 0 && !addingSite && <EmptyState message="No sites found for this organisation." />}

            {addingSite ? (
              <SiteForm
                organisationId={Number(selectedOrg)}
                onSave={() => { setAddingSite(false); loadSites(); }}
                onCancel={() => setAddingSite(false)}
              />
            ) : (
              <button className="btn btn-primary" onClick={() => setAddingSite(true)}>Add Site</button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
